import { useEffect, useRef, useState } from "react";

/**
 * Orientation-gated remount signal for the docked TransformWrapper.
 *
 * Ported from v3's useResizingLoaderHook, but hardened against the "blink on
 * load" bug: v3 bumped `rwKey` (a hard TransformWrapper remount) on EVERY resize,
 * including the spurious resize / `visualViewport` event browsers fire right
 * after mount — which remounted the whole canvas once on load.
 *
 * Ordinary resizes are already handled smoothly by DockedVisualizerWrapper's
 * `syncToViewport` RAF (its effect re-runs on canvasSize change and re-fits the
 * canvas). A hard remount is only needed for a true portrait<->landscape flip,
 * where react-zoom-pan-pinch can otherwise keep a stale transform. So we bump
 * `rwKey` ONLY when the orientation actually changes — never on the initial
 * value, never on same-orientation resizes.
 */
type Orientation = "portrait" | "landscape";

function getOrientation(): Orientation {
  if (typeof window === "undefined") return "landscape";
  return window.innerHeight > window.innerWidth ? "portrait" : "landscape";
}

export interface ResizingLoaderState {
  rwKey: number;
  isResizing: boolean;
}

export default function useResizingLoaderHook(): ResizingLoaderState {
  const [rwKey, setRwKey] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const orientationRef = useRef<Orientation>(getOrientation());

  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;

    const onResize = () => {
      const next = getOrientation();
      // Ignore resizes that don't flip orientation (this includes the spurious
      // on-load resize) so we never remount just from mounting or minor reflow.
      if (next === orientationRef.current) return;
      orientationRef.current = next;

      setIsResizing(true);
      clearTimeout(t);
      t = setTimeout(() => {
        setRwKey((k) => k + 1); // hard reset (remount TransformWrapper) on true rotate
        setIsResizing(false);
      }, 200);
    };

    window.addEventListener("resize", onResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onResize);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onResize);
      }
      clearTimeout(t);
    };
  }, []);

  return { rwKey, isResizing };
}
